import React, { useEffect, useState } from "react";

import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import moment from "moment";

import {
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import {
  createManualPayment,
  fetchManualPayments,
  updateManualPayment,
  uploadFileStatic,
} from "../api";

const PAGE_SIZE = 10;

const PRODUCT_OPTIONS = [
  { label: "Freedom Setup", value: "freedom" },
  { label: "Enterprise Setup", value: "enterprise" },
];

const PLAN_OPTIONS = {
  freedom: [
    { label: "Freedom Basic", value: "tier1" },
    { label: "Freedom Pro", value: "tier2" },
    { label: "Freedom Advanced", value: "tier3" },
  ],
  enterprise: [
    { label: "Enterprise Basic", value: "tier1" },
    { label: "Enterprise Pro", value: "tier2" },
    { label: "Enterprise Advanced", value: "tier3" },
    { label: "Enterprise Ultimate", value: "tier4" },
  ],
};

const STATUS_OPTIONS = [
  { label: "Open", value: "open" },
  { label: "Completed", value: "completed" },
];

const STATUS_STYLE = {
  open: { color: "#2563eb" },
  completed: { color: "#059669" },
};

const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = ["pdf", "png", "jpg", "jpeg"];
const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
];

const getFileExtension = (fileName = "") => {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const getAttachmentTypeFromFile = (file) => {
  const ext = getFileExtension(file.name);
  if (ALLOWED_EXTENSIONS.includes(ext)) return ext;
  if (file.type === "application/pdf") return "pdf";
  if (file.type === "image/png") return "png";
  if (file.type === "image/jpeg" || file.type === "image/jpg") return "jpeg";
  return ext || "file";
};

const renderAttachmentIcon = (type) => {
  if (type === "pdf") return <FilePdfOutlined style={{ color: "#dc2626" }} />;
  if (["png", "jpg", "jpeg"].includes(type)) {
    return <FileImageOutlined style={{ color: "#2563eb" }} />;
  }
  return <PaperClipOutlined style={{ color: "#6b7280" }} />;
};

const getProductLabel = (value) =>
  PRODUCT_OPTIONS.find((p) => p.value === value)?.label || value || "-";

const getPlanLabel = (platformSetup, tier) =>
  PLAN_OPTIONS[platformSetup]?.find((p) => p.value === tier)?.label ||
  tier ||
  "-";

const getValidityLabel = (days) => {
  if (days === 0) return "Lifetime";
  if (!days) return "-";
  // if (days % 30 === 0) {
  //   const months = days / 30;
  //   return `${months} Month${months > 1 ? "s" : ""}`;
  // }
  return `${days} Days`;
};

const ManualPayments = () => {
  const [loader, setLoader] = useState(true);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [payments, setPayments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isLifetimeValidity, setIsLifetimeValidity] = useState(false);
  const [form] = Form.useForm();

  const loadPayments = async () => {
    setLoader(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        page,
        sortOrder: "desc",
      };
      if (searchQuery?.trim()) params.search = searchQuery.trim();

      const response = await fetchManualPayments(params);
      const items = response?.items || response?.result?.items || [];
      const total =
        response?.totalItems || response?.result?.totalItems || items.length;

      setPayments(items);
      setTotalItems(total);
    } catch (error) {
      console.error("Error fetching manual payments:", error);
      message.error(error.message || "Failed to fetch manual payments");
      setPayments([]);
      setTotalItems(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [page, searchQuery]);

  const handleOpenAddModal = () => {
    form.resetFields();
    setAttachments([]);
    setIsLifetimeValidity(false);
    setIsModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsModalOpen(false);
    form.resetFields();
    setAttachments([]);
    setIsLifetimeValidity(false);
  };

  const handleProductChange = () => {
    form.setFieldsValue({ tier: undefined, validityInDays: undefined });
    setIsLifetimeValidity(false);
  };

  const handlePlanChange = (tier) => {
    const platformSetup = form.getFieldValue("platformSetup");

    // Check if selected plan is Freedom Basic or Enterprise Basic (tier1)
    if (
      tier === "tier1" &&
      (platformSetup === "freedom" || platformSetup === "enterprise")
    ) {
      setIsLifetimeValidity(true);
      form.setFieldsValue({ validityInDays: "lifetime" });
    } else {
      setIsLifetimeValidity(false);
      form.setFieldsValue({ validityInDays: undefined });
    }
  };

  const handleBeforeAttachmentUpload = (file, fileList) => {
    const ext = getFileExtension(file.name);
    const isAllowedExt = ALLOWED_EXTENSIONS.includes(ext);
    const isAllowedMime =
      !file.type || ALLOWED_MIME_TYPES.includes(file.type.toLowerCase());

    if (!isAllowedExt || !isAllowedMime) {
      message.error(
        `"${file.name}" is not a supported file type. Only PDF, JPG, JPEG and PNG are allowed.`
      );
      return Upload.LIST_IGNORE;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      message.error(
        `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`
      );
      return Upload.LIST_IGNORE;
    }

    const batchIndex = fileList?.findIndex((f) => f.uid === file.uid) ?? 0;
    const targetSlot = attachments.length + batchIndex;
    if (targetSlot >= MAX_ATTACHMENTS) {
      if (batchIndex === 0 || file === fileList?.[0]) {
        message.error(
          `You can attach a maximum of ${MAX_ATTACHMENTS} files per entry.`
        );
      }
      return Upload.LIST_IGNORE;
    }

    setAttachments((prev) => {
      if (prev.length >= MAX_ATTACHMENTS) return prev;
      return [
        ...prev,
        {
          uid: file.uid || `${file.name}-${Date.now()}`,
          file,
          fileName: file.name,
          type: getAttachmentTypeFromFile(file),
        },
      ];
    });
    return false;
  };

  const handleRemoveAttachment = (uid) => {
    setAttachments((prev) => prev.filter((a) => a.uid !== uid));
  };

  const uploadSingleAttachment = async (att) => {
    const formData = new FormData();
    formData.append("file", att.file);
    const resp = await uploadFileStatic(formData);
    const url =
      resp?.result?.files?.[0] ||
      resp?.result?.url ||
      resp?.url ||
      resp?.result;
    if (!url || (resp?.code !== undefined && resp.code !== 0)) {
      throw new Error(
        resp?.message || `Failed to upload attachment "${att.fileName}"`
      );
    }
    return { type: att.type, fileName: att.fileName, url };
  };

  const uploadAttachments = () =>
    Promise.all(attachments.map(uploadSingleAttachment));

  const handleAddEntry = async (values) => {
    setSubmitLoader(true);
    try {
      const uploadedAttachments = await uploadAttachments();

      // Convert validity: if lifetime, send 0; otherwise use days as entered
      const validityInDays =
        isLifetimeValidity || values.validityInDays === "lifetime"
          ? 0
          : Number(values.validityInDays);

      const payload = {
        name: values.name?.trim(),
        email: values.email?.trim(),
        phone: values.phone?.trim(),
        platformSetup: values.platformSetup,
        tier: values.tier,
        amount: Number(values.amount),
        validityInDays,
      };
      if (values.tdsId?.trim()) payload.tdsId = values.tdsId.trim();
      if (values.gstId?.trim()) payload.gstId = values.gstId.trim();
      if (uploadedAttachments.length > 0) {
        payload.attachments = uploadedAttachments;
      }

      const response = await createManualPayment(payload);

      if (response?._id || response?.result?._id || response?.success) {
        message.success("Manual payment entry created successfully!");
        handleCloseAddModal();
        if (page !== 1) {
          setPage(1);
        } else {
          loadPayments();
        }
      } else {
        message.error(response?.message || "Failed to create manual payment");
      }
    } catch (error) {
      console.error("Error creating manual payment:", error);
      message.error(error.message || "Failed to create manual payment");
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleStatusChange = async (record, newStatus) => {
    if (record.status === newStatus) return;
    setStatusUpdatingId(record._id);
    try {
      await updateManualPayment(record._id, { status: newStatus });
      message.success("Status updated successfully");
      setPayments((prev) =>
        prev.map((p) =>
          p._id === record._id ? { ...p, status: newStatus } : p
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
      message.error(error.message || "Failed to update status");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      width: 180,
      fixed: "left",
      render: (text) => (
        <Typography.Text style={{ fontWeight: 600 }}>
          {text || "-"}
        </Typography.Text>
      ),
    },
    {
      title: "DATE",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (value) =>
        value ? (
          <Typography.Text style={{ fontSize: 13, color: "#4b5563" }}>
            {moment(value).format("DD MMM YYYY, hh:mm a")}
          </Typography.Text>
        ) : (
          "-"
        ),
    },
    {
      title: "CONTACT",
      dataIndex: "phone",
      key: "phone",
      width: 280,
      render: (text, record) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Typography.Text>{text}</Typography.Text>
            {text && text !== "-" && (
              <Tooltip title="Copy Phone">
                <CopyOutlined
                  style={{ color: "#9ca3af", cursor: "pointer", fontSize: 12 }}
                  onClick={() => {
                    if (!text || text === "-") return;
                    navigator.clipboard.writeText(text?.trim());
                    message.success("Phone copied!");
                  }}
                />
              </Tooltip>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Typography.Text>
            {record.email && record.email !== "-" && (
              <Tooltip title="Copy Email">
                <CopyOutlined
                  style={{ color: "#9ca3af", cursor: "pointer", fontSize: 12 }}
                  onClick={() => {
                    navigator.clipboard.writeText(record.email);
                    message.success("Email copied!");
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "PRODUCT",
      dataIndex: "platformSetup",
      key: "platformSetup",
      width: 150,
      render: (value) => getProductLabel(value),
    },
    {
      title: "PLAN",
      dataIndex: "tier",
      key: "tier",
      width: 170,
      render: (tier, record) => getPlanLabel(record.platformSetup, tier),
    },
    {
      title: "PRICE",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      render: (value) => (
        <Typography.Text style={{ fontWeight: 600 }}>
          ₹{(value || 0).toLocaleString("en-IN")}
        </Typography.Text>
      ),
    },
    {
      title: "VALIDITY",
      dataIndex: "validityInDays",
      key: "validityInDays",
      width: 120,
      render: (value) => getValidityLabel(value),
    },
    {
      title: "TDS ID",
      dataIndex: "tdsId",
      key: "tdsId",
      width: 150,
      render: (value) => value || "-",
    },
    {
      title: "GST ID",
      dataIndex: "gstId",
      key: "gstId",
      width: 160,
      render: (value) => value || "-",
    },
    {
      title: "CREATED BY",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 180,
      render: (value) => {
        if (!value || typeof value !== "object") return "-";
        const label = value.name || value.email;
        if (!label) return "-";
        return (
          <Tooltip title={value.email || ""}>
            <Typography.Text style={{ fontSize: 13 }}>{label}</Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: "ATTACHMENTS",
      dataIndex: "attachments",
      key: "attachments",
      width: 150,
      render: (list) => {
        if (!Array.isArray(list) || list.length === 0) return "-";
        return (
          <Space size={8}>
            {list.map((att, idx) => (
              <Tooltip
                key={att.url || `${att.fileName}-${idx}`}
                title={att.fileName || att.url}
              >
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 18, lineHeight: 1 }}
                >
                  {renderAttachmentIcon(att.type)}
                </a>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 160,
      fixed: "right",
      render: (status, record) => {
        const style = STATUS_STYLE[status] || STATUS_STYLE.open;
        return (
          <Select
            value={status || "open"}
            onChange={(value) => handleStatusChange(record, value)}
            loading={statusUpdatingId === record._id}
            disabled={statusUpdatingId === record._id}
            bordered
            style={{
              width: 140,
              fontWeight: 600,
              textTransform: "uppercase",
              color: style.color,
            }}
            dropdownStyle={{ textTransform: "uppercase" }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                <span
                  style={{
                    color: STATUS_STYLE[opt.value]?.color || "#111827",
                    fontWeight: 600,
                  }}
                >
                  {opt.label.toUpperCase()}
                </span>
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
  ];

  const planOptionsForSelectedProduct = Form.useWatch("platformSetup", form);

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Title level={2} style={{ margin: 0 }}>
              Manual Payments
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
              Manage manual payment requests and offline transactions.
            </Typography.Text>
          </Col>
          <Col>
            <Space size="middle">
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={handleOpenAddModal}
                style={{
                  background: "#7c3aed",
                  borderColor: "#7c3aed",
                }}
              >
                Add Entry
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <FiltersWrapper style={{ justifyContent: "space-between" }}>
        <div
          style={{ display: "flex", gap: 10, alignItems: "center", flex: 1 }}
        >
          <CustomSearchbar
            placeholder="Search by name, contact..."
            searchQuery={searchQuery}
            setSearchQuery={(value) => {
              setSearchQuery(value);
              setPage(1);
            }}
            style={{ flex: 1, width: "100%" }}
          />
          <Tooltip title="Refresh">
            <Button size="large" onClick={loadPayments}>
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      </FiltersWrapper>

      <Table
        columns={columns}
        dataSource={payments}
        rowKey="_id"
        loading={loader}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: totalItems,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} entries`,
          onChange: (newPage) => setPage(newPage),
        }}
        locale={{
          emptyText: (
            <div style={{ padding: "40px 0", textAlign: "center" }}>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 16, display: "block", marginBottom: 8 }}
              >
                No manual payments found
              </Typography.Text>
              <Typography.Text type="secondary">
                Create a new manual payment by clicking the &quot;Add
                Entry&quot; button above.
              </Typography.Text>
            </div>
          ),
        }}
        scroll={{ x: 2080 }}
      />

      <Modal
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            Add Manual Payment Entry
          </Typography.Title>
        }
        open={isModalOpen}
        onCancel={handleCloseAddModal}
        footer={null}
        width={620}
        destroyOnClose
        centered
        closeIcon={<CloseOutlined style={{ color: "#9ca3af" }} />}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddEntry}
          requiredMark={false}
        >
          <Form.Item
            label={
              <span style={{ fontWeight: 600, color: "#6b7280" }}>
                CUSTOMER NAME <span style={{ color: "#ef4444" }}>*</span>
              </span>
            }
            name="name"
            rules={[{ required: true, message: "Please enter customer name" }]}
            style={{ marginBottom: 20 }}
          >
            <Input
              size="large"
              placeholder="e.g. Aman Gupta"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    CONTACT PHONE <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                }
                name="phone"
                rules={[
                  { required: true, message: "Please enter contact phone" },
                  {
                    pattern: /^\+?[0-9\s-]{6,20}$/,
                    message: "Please enter a valid phone number",
                  },
                ]}
                style={{ marginBottom: 20 }}
              >
                <Input
                  size="large"
                  placeholder="e.g. +91 99887 76655"
                  style={{ borderRadius: 8 }}
                  inputMode="tel"
                  onKeyPress={(e) => {
                    if (!/[0-9+\s-]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (!/^[0-9+\s-]+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    CONTACT EMAIL <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                }
                name="email"
                rules={[
                  { required: true, message: "Please enter contact email" },
                  {
                    pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                    message: "Please enter a valid email",
                  },
                ]}
                style={{ marginBottom: 20 }}
              >
                <Input
                  size="large"
                  placeholder="e.g. aman@gmail.com"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    PRODUCT <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                }
                name="platformSetup"
                rules={[{ required: true, message: "Please select a product" }]}
                style={{ marginBottom: 20 }}
              >
                <Select
                  size="large"
                  placeholder="Select product"
                  onChange={handleProductChange}
                  options={PRODUCT_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    PLAN <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                }
                name="tier"
                rules={[{ required: true, message: "Please select a plan" }]}
                style={{ marginBottom: 20 }}
              >
                <Select
                  size="large"
                  placeholder="Select plan"
                  disabled={!planOptionsForSelectedProduct}
                  options={PLAN_OPTIONS[planOptionsForSelectedProduct] || []}
                  onChange={handlePlanChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    PRICE (INR) <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                }
                name="amount"
                rules={[
                  { required: true, message: "Please enter price" },
                  {
                    type: "number",
                    min: 1,
                    message: "Price must be greater than 0",
                  },
                ]}
                style={{ marginBottom: 20 }}
              >
                <InputNumber
                  size="large"
                  placeholder="e.g. 125000"
                  style={{ width: "100%", borderRadius: 8 }}
                  min={0}
                  controls={false}
                  formatter={(value) =>
                    value
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                  parser={(value) => (value ? value.replace(/,/g, "") : "")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    VALIDITY <small>(in days)</small>{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                }
                name="validityInDays"
                rules={[
                  {
                    required: !isLifetimeValidity,
                    message: "Please enter validity in days!",
                  },
                  {
                    validator: (_, value) => {
                      if (isLifetimeValidity || value === "lifetime") {
                        return Promise.resolve();
                      }
                      if (
                        value === undefined ||
                        value === null ||
                        value === ""
                      ) {
                        return Promise.resolve();
                      }
                      if (!Number.isInteger(Number(value))) {
                        return Promise.reject(
                          new Error("Validity must be a whole number")
                        );
                      }
                      if (Number(value) < 1) {
                        return Promise.reject(
                          new Error("Validity must be at least 1 day")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                style={{ marginBottom: 20 }}
              >
                {isLifetimeValidity ? (
                  <Input
                    size="large"
                    value="Lifetime"
                    disabled
                    style={{
                      borderRadius: 8,
                      padding: 8,
                      textTransform: "capitalize",
                    }}
                  />
                ) : (
                  <InputNumber
                    size="large"
                    placeholder="Enter days"
                    min={1}
                    precision={0}
                    step={1}
                    controls={false}
                    inputMode="numeric"
                    style={{ width: "100%", borderRadius: 8 }}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData("text");
                      if (!/^\d+$/.test(pasted)) {
                        e.preventDefault();
                      }
                    }}
                    parser={(value) =>
                      value ? value.replace(/[^\d]/g, "") : ""
                    }
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    TDS ID (OPTIONAL)
                  </span>
                }
                name="tdsId"
                style={{ marginBottom: 24 }}
              >
                <Input
                  size="large"
                  placeholder="e.g. TDS99881"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    GST ID (OPTIONAL)
                  </span>
                }
                name="gstId"
                style={{ marginBottom: 24 }}
              >
                <Input
                  size="large"
                  placeholder="e.g. GST77621"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#6b7280" }}>
                ATTACHMENTS (OPTIONAL)
              </span>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginTop: 2 }}
              >
                Up to {MAX_ATTACHMENTS} files. PDF, JPG, JPEG, PNG. Max{" "}
                {MAX_FILE_SIZE_MB}MB each.
              </Typography.Text>
            </div>
            <Upload
              multiple
              showUploadList={false}
              beforeUpload={handleBeforeAttachmentUpload}
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              disabled={submitLoader || attachments.length >= MAX_ATTACHMENTS}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={submitLoader || attachments.length >= MAX_ATTACHMENTS}
                style={{ borderRadius: 8 }}
              >
                Add Attachment ({attachments.length}/{MAX_ATTACHMENTS})
              </Button>
            </Upload>
            {attachments.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {attachments.map((att) => (
                  <div
                    key={att.uid}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 10px",
                      borderRadius: 6,
                      background: "#f3f4f6",
                    }}
                  >
                    <Space size={8} style={{ overflow: "hidden" }}>
                      {renderAttachmentIcon(att.type)}
                      <Typography.Text
                        ellipsis
                        style={{ maxWidth: 420, fontSize: 13 }}
                      >
                        {att.fileName}
                      </Typography.Text>
                    </Space>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveAttachment(att.uid)}
                      disabled={submitLoader}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={handleCloseAddModal}
                disabled={submitLoader}
                style={{ borderRadius: 8, minWidth: 110 }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitLoader}
                style={{
                  background: "#7c3aed",
                  borderColor: "#7c3aed",
                  borderRadius: 8,
                  minWidth: 140,
                }}
              >
                Add Entry
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ManualPayments;
